import React, { useMemo } from 'react';
import { Box, ButtonBase, makeStyles } from '@material-ui/core';
import _ from 'lodash';
import { ClickIcon, EditIcon } from '../../../../../components/Icon';
import FemaleIcon from '../../../../../static/images/GENDER_FEMALE.png';
import MaleIcon from '../../../../../static/images/GENDER_MALE.png';
import XmaleIcon from '../../../../../static/images/GENDER_X.png';
import { classNames, initValue } from '../../../../../utils';
import Text from '../../../../../components/Text';
import { MdroIcon } from '../../../../../components/MdroInfo';
import { Triangle } from '../../Triangle';
import unLinkIcon from '../../../../../static/images/unlink.png';

const useStyles = makeStyles(() => ({
  root: ({ isSelected, showActionButtons }) => ({
    position: 'relative',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    borderRadius: '8px',
    border: 'solid 1px #bad2ff',
    boxShadow: isSelected && !showActionButtons ? 'inset 0 0 0 2px #538fff' : 'none',
    boxSizing: 'border-box',
  }),
  bedNoPlate: ({ bedNoBorderColor, patientCardBackgroundColor }) => ({
    width: '3.4rem',
    height: '2.5rem',
    boxSizing: 'border-box',
    borderLeft: `solid 1px ${bedNoBorderColor}`,
    borderBottom: `solid 1px ${bedNoBorderColor}`,
    borderTop: 'solid 1px transparent',
    borderRight: 'solid 1px transparent',
    borderRadius: '1px 1px 1px 4px',
    backgroundColor: patientCardBackgroundColor,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  }),
  bedNoText: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '1rem',
    lineHeight: 1.2,
    color: '#2e4469',
    // whiteSpace: 'nowrap',
  },
  patientCard: ({ showActionButtons, patientCardBackgroundColor }) => ({
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    borderRadius: showActionButtons ? '8px 0 0 8px' : '8px',
    backgroundColor: patientCardBackgroundColor,
    padding: '0.2rem 0.285rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    overflow: 'hidden',
    gap: '0.285rem',
    cursor: 'pointer',
    transform: showActionButtons ? 'translateX(-10.142rem)' : 'none',
    transition: 'transform 160ms ease-out',
  }),
  actionButton: {
    position: 'absolute',
    top: 0,
    width: '5.071rem',
    height: '100%',
    color: '#ffffff',
    zIndex: 1,
  },
  moreButton: {
    right: '5.071rem',
    backgroundColor: '#538fff',
    '&:hover': {
      backgroundColor: '#3f7fed',
    },
  },
  deleteButton: {
    right: 0,
    borderRadius: '0 8px 8px 0',
    backgroundColor: '#ff655c',
    '&:hover': {
      backgroundColor: '#e94c43',
    },
  },
  moreIcon: {
    fontSize: '1.286rem',
  },
  unlinkIcon: {
    width: '1.428rem',
    height: '1.428rem',
  },
  mdroBox: {
    minWidth: '2rem',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  genderIcon: ({ genderColor }) => ({
    width: '1.2rem',
    color: genderColor,
  }),
  femaleIcon: ({ genderColor }) => ({
    width: '1.1rem',
    color: genderColor,
  }),
  age: ({ genderColor }) => ({
    fontFamily: 'NotoSans-Bold',
    fontSize: '1rem',
    color: genderColor,
    textAlign: 'center',
  }),
  caseNo: ({ genderColor }) => ({
    fontFamily: 'NotoSans-Bold',
    color: genderColor,
  }),
  patientNameBox: {
    width: '105%',
    minWidth: 0,
    maxWidth: '105%',
  },
  patientTextColumn: {
    flex: 1,
    minWidth: 0,
    maxWidth: '100%',
  },
  patientName: {
    color: '#2E4469',
    fontFamily: 'HA_MingLiu, NotoSans-Medium',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  patientNameWithoutDetails: {
    overflow: 'visible',
    textOverflow: 'clip',
    position: 'relative',
    zIndex: 1,
  },
  patientBedFigure: {
    width: '2.5rem',
    minWidth: '2.5rem',
    minHeight: '3.571rem',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
  },
  pendingLabel: {
    width: '2.5rem',
    height: '1.107rem',
    borderRadius: '0.285rem 0.285rem 0 0',
    fontFamily: 'NotoSans-Bold',
    fontSize: '0.55rem',
    color: '#2e4469',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '0.07rem solid #7d86a9',
  },
  wardInfo: {
    borderRadius: '0 0 0.285rem 0.285rem',
    border: '0.07rem solid #7d86a9',
    borderTop: 0,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-evenly',
    alignItems: 'center',
  },
  wardText: {
    fontFamily: 'NotoSans-Bold',
    color: '#7D86A9',
    textAlign: 'center',
    fontWeight: 'bold',
  },
  warningIcon: {
    flexShrink: 0,
  },
  clickIcon: {
    width: '1.8rem',
    height: '1.7rem',
  },
  clickBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.2rem',
    height: '100%',
    justifyContent: 'center',
  },
}));

const GENDER_COLOR = {
  F: '#f54c9d',
  M: '#538fff',
  X: '#7b80ff',
  default: '#538fff',
};

const getGenderColor = (gender) => GENDER_COLOR[gender] || GENDER_COLOR.default;

// 按E-Admission waiting time的分段返回床卡背景色。
const getPatientBedBackgroundColor = (waitTimestamp) => {
  if (waitTimestamp === null || waitTimestamp === undefined || waitTimestamp === '' || waitTimestamp < 0) {
    return '#ffffff';
  }
  if (waitTimestamp <= 14400) {
    return 'white';
  } else if (waitTimestamp <= 28800) {
    return '#fff4cd';
  } else if (waitTimestamp <= 43200) {
    return '#feeceb';
  }
  return 'rgba(240,240,240)';
};

const formatWaitingTime = (waitingTime) => {
  return waitingTime;
  // if (!waitingTime && waitingTime !== 0) {
  //   return '';
  // }

  // const totalSeconds = getWaitingSeconds(waitingTime);

  // if (totalSeconds === null || totalSeconds < 0) {
  //   return '';
  // }

  // const hours = Math.floor(totalSeconds / 3600);
  // const minutes = Math.floor((totalSeconds % 3600) / 60);

  // return `${hours}:${String(minutes).padStart(2, '0')}`;
};

const getShortChineseName = (name) => {
  const nameChars = Array.from(name ?? '');
  return nameChars.length > 5 ? `${nameChars.slice(0, 5).join('')}...` : name;
};

export default function PatientBed({
  patientInfo,
  bedName = '',
  editMode,
  hasPatient,
  isQueuebar = false,
  isSelected = false,
  handleSelect = null,
  handleDeletePatient = null,
  handleOpenPatientDetail = null,
  viewType = '',
  showDetails = true,
  patientInfoViewType = '',
}) {
  const genderColor = getGenderColor(patientInfo?.sex);
  const bedNoBorderColor = useMemo(() => {
    if (isQueuebar) {
      return '#92C951';
    } else if (hasPatient) {
      return '#538fff';
    } else {
      return '#bad2ff';
    }
  }, [hasPatient, isQueuebar]);
  const showActionButtons = isSelected && !isQueuebar;
  const patientCardBackgroundColor = getPatientBedBackgroundColor(patientInfo?.waitTimestamp);
  const classes = useStyles({
    bedNoBorderColor,
    genderColor,
    isSelected,
    patientCardBackgroundColor,
    showActionButtons,
  });
  const waitingTime = formatWaitingTime(patientInfo?.waitTime);
  const mdroInfos = useMemo(() => _.orderBy(patientInfo?.mdroInfos, ['alertDesc'], ['asc']), [patientInfo?.mdroInfos]);
  // AED和SOPD的平面图与Queuebar共用各自的详情开关；其他入口保持原有资料展示。
  const isLocationViewPatientBed = ['AED', 'SOPD'].includes(patientInfoViewType);
  const shouldShowDetails = !isLocationViewPatientBed || showDetails;
  const detailFontSize = isLocationViewPatientBed ? '0.7rem' : '0.8rem';
  const englishNameFontSize = isLocationViewPatientBed ? '0.9rem' : '0.8rem';
  const chineseNameFontSize = isLocationViewPatientBed ? (showDetails ? '1.7rem' : '1.81rem') : '1.1rem';
  const chineseNameLineHeight = isLocationViewPatientBed ? 1.12 : undefined;
  const chineseName = shouldShowDetails
    ? patientInfo?.patientName?.nameZh
    : getShortChineseName(patientInfo?.patientName?.nameZh);
  const isWardReadyBusStop = patientInfo?.busStopStep === 'Ward Ready';

  // 点击床位区域时选中当前病人。
  const handleCardClick = (event) => {
    event.stopPropagation();
    if (handleSelect) {
      handleSelect();
    }
  };

  // Queuebar 点击病人资料时打开详情页。
  const handlePatientInfoClick = (event) => {
    if (!isQueuebar) return;
    event.stopPropagation();

    if (isQueuebar && handleOpenPatientDetail) {
      handleOpenPatientDetail(patientInfo);
    }
  };

  const BedNoPlate = ({ waitingTime }) => (
    <Box className={classes.clickBox} onClick={isQueuebar ? handleCardClick : undefined}>
      {waitingTime && (
        <Text fontFamily="NotoSans-Bold" fontSize="0.928rem" color="#7D86A9">
          {initValue(waitingTime, '')}
        </Text>
      )}
      <Box className={classes.bedNoPlate}>
        {isQueuebar ? (
          <ClickIcon className={classes.clickIcon} />
        ) : (
          <span className={classes.bedNoText}>{bedName}</span>
        )}
      </Box>
    </Box>
  );

  const PatientInfo = () => {
    return (
      <Box display="flex" gridGap="0.285rem" flex={1} minWidth={0} alignItems="center" onClick={handlePatientInfoClick}>
        <Box>
          {patientInfo?.priority === 1 && (
            <Box mb="0.3rem">
              <Triangle color="rgba(215,65,37)" number={patientInfo?.priority} className={classes.warningIcon} />
            </Box>
          )}
          <Box display="flex" alignItems="center" justifyContent="center">
            {patientInfo?.sex === 'M' && <img className={classes.genderIcon} src={MaleIcon} alt="MaleIcon" />}
            {patientInfo?.sex === 'F' && <img className={classes.femaleIcon} src={FemaleIcon} alt="FemaleIcon" />}
            {patientInfo?.sex === 'X' && <img className={classes.genderIcon} src={XmaleIcon} alt="XmaleIcon" />}
          </Box>
          <Box className={classes.age}>{patientInfo?.age}</Box>
        </Box>
        <Box display="flex" flex={1} minWidth={0} alignItems="flex-end" bgcolor={isWardReadyBusStop && '#F4FF72'}>
          <Box display="flex" flexDirection="column" className={classes.patientTextColumn}>
            {shouldShowDetails && (
              <Box display="flex" gridGap="0.285rem">
                <Text fontFamily="NotoSans-Bold" fontSize={detailFontSize} color={getGenderColor(patientInfo?.sex)}>
                  {initValue(patientInfo?.caseNo, '')}
                </Text>
                {patientInfo?.specialtyCode && (
                  <Text fontFamily="NotoSans-Bold" fontSize={detailFontSize} color="#7D86A9">
                    {initValue(patientInfo?.specialtyCode, '')}
                  </Text>
                )}
                {patientInfo?.subSpecialtyCode && (
                  <Text fontFamily="NotoSans-Bold" fontSize={detailFontSize} color="#7D86A9">
                    {initValue(patientInfo?.subSpecialtyCode, '')}
                  </Text>
                )}
              </Box>
            )}
            <Box className={classes.patientNameBox}>
              <Box className={classes.patientName} fontSize={englishNameFontSize}>
                {patientInfo?.patientName?.nameEn}
              </Box>
              <Box
                className={classNames(classes.patientName, !shouldShowDetails && classes.patientNameWithoutDetails)}
                fontSize={chineseNameFontSize}
                lineHeight={chineseNameLineHeight}
                fontWeight="700"
              >
                {chineseName}
              </Box>
            </Box>
            {/* hnNo和admRegHn是同一个，AED View是hnNo，SOPD View是admRegHn */}
            {shouldShowDetails && patientInfo?.hnNo && (
              <Text color={genderColor} fontSize={detailFontSize}>
                {initValue(patientInfo?.hnNo, '')}
              </Text>
            )}
            {shouldShowDetails && patientInfo?.admRegHn && (
              <Text color={genderColor} fontSize={detailFontSize}>
                {initValue(patientInfo?.admRegHn, '')}
              </Text>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  const PatientBedFigure = () => {
    const wardInfo = patientInfo?.admWardInfo;

    return (
      <Box className={classes.patientBedFigure}>
        <Box className={classes.pendingLabel} bgcolor={patientInfo?.colour}>
          {initValue(patientInfo?.label, '')}
        </Box>
        <Box className={classes.wardInfo}>
          <Box className={classes.wardText} fontSize="1rem">
            {initValue(wardInfo?.wardName, '')}
          </Box>
          <Box className={classes.wardText} fontSize="0.714rem">
            {initValue(wardInfo?.hospCode, '')}
          </Box>
        </Box>
      </Box>
    );
  };

  const handleDeleteClick = (event) => {
    event.stopPropagation();

    if (handleDeletePatient) {
      handleDeletePatient();
    }
  };

  const handleMoreClick = (event) => {
    event.stopPropagation();

    if (handleOpenPatientDetail) {
      handleOpenPatientDetail(patientInfo);
    }
  };

  return (
    <Box
      className={classes.root}
      style={{
        border: isSelected && isQueuebar ? 'solid 2px #538fff' : '',
      }}
    >
      <Box
        className={classes.patientCard}
        onClick={isQueuebar ? undefined : handleCardClick}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <BedNoPlate waitingTime={waitingTime} />
        <PatientInfo />
        <Box className={classes.mdroBox}>
          {_.map(mdroInfos, (item, index) => (
            <MdroIcon
              value={item.alertDesc}
              confirmedInd={item.confirmedInd}
              width="2rem"
              height="fit-content"
              fontSize="0.542rem"
              type="bedstatus"
              key={`${item.alertDesc}-${item.confirmedInd}-${index}`}
            />
          ))}
        </Box>
        <PatientBedFigure />
      </Box>
      {showActionButtons && (
        <ButtonBase
          className={classNames(classes.actionButton, classes.moreButton)}
          onClick={handleMoreClick}
          onMouseDown={(event) => event.stopPropagation()}
          aria-label="Open patient detail"
        >
          <Box display="flex" flexDirection="column" alignItems="center" gridGap="0.2rem">
            <Box fontSize="0.75rem" fontFamily="NotoSans-Medium">
              Edit
            </Box>
            <EditIcon className={classes.moreIcon} />
          </Box>
        </ButtonBase>
      )}
      {showActionButtons && (
        <ButtonBase
          className={classNames(classes.actionButton, classes.deleteButton)}
          onClick={handleDeleteClick}
          onMouseDown={(event) => event.stopPropagation()}
          aria-label="Delete patient"
        >
          <Box display="flex" flexDirection="column" alignItems="center" gridGap="0.2rem">
            <Box fontSize="0.75rem" fontFamily="NotoSans-Medium">
              Delink location
            </Box>
            <Box className={classes.unlinkIcon}>
              <img src={unLinkIcon} style={{ width: '100%', height: '100%' }} alt="unlink-icon" />
            </Box>
          </Box>
        </ButtonBase>
      )}
    </Box>
  );
}
